import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import { categorizeFirestoreError } from './firebaseErrors';

type UnknownRecord = Record<string, unknown>;

type AuditEventLegacy = {
  action: string;
  allowed: boolean;
  userId?: string | null;
  reason?: string | null;
  resource: string;
  resourceId?: string | null;
  metadata?: UnknownRecord | null;
};

type AuditEventV2 = {
  action: string;
  allowed: boolean;
  actor: {
    uid: string;
    role: string;
  };
  resource: {
    type: string;
    id: string;
  };
  metadata?: UnknownRecord;
};

type AuditEventInput = AuditEventLegacy | AuditEventV2;

const isV2 = (event: AuditEventInput): event is AuditEventV2 =>
  typeof (event as AuditEventV2).actor?.uid === 'string' &&
  typeof (event as AuditEventV2).resource?.type === 'string';

const stripUndefinedDeep = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(stripUndefinedDeep).filter((v) => v !== undefined);
  }
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    Object.entries(obj).forEach(([k, v]) => {
      if (v === undefined) return;
      const next = stripUndefinedDeep(v);
      if (next === undefined) return;
      out[k] = next;
    });
    return out;
  }
  return value;
};

export const logAuditEvent = async (event: AuditEventInput) => {
  const docData: UnknownRecord = isV2(event)
    ? {
        action: event.action,
        allowed: event.allowed,
        userId: event.actor.uid,
        resource: event.resource.type,
        resourceId: event.resource.id,
        reason:
          typeof (event.metadata as UnknownRecord | undefined)?.reason === 'string'
            ? ((event.metadata as UnknownRecord).reason as string)
            : null,
        metadata: event.metadata ?? null,
        timestamp: serverTimestamp(),
      }
    : {
        action: event.action,
        allowed: event.allowed,
        userId: event.userId ?? null,
        resource: event.resource,
        resourceId: event.resourceId ?? null,
        reason: event.reason ?? null,
        metadata: event.metadata ?? null,
        timestamp: serverTimestamp(),
      };

  try {
    await addDoc(collection(db, 'auditLogs'), stripUndefinedDeep(docData) as UnknownRecord);
  } catch (error) {
    const category = categorizeFirestoreError(error);
    if (category === 'permission-denied') console.debug('Failed to log audit event', error);
    else console.warn('Failed to log audit event', error);
  }
};
